import { DocumentNode, gql } from '@apollo/client'

const POLL_FRAGMENT: DocumentNode = gql`
    fragment PollFields on Poll {
        id
        type
        deadline
        ratingDisplay
        hasVotes
        anonymous
        options {
            id
            text
            order
        }
        myAnswer {
            textAnswer
            rating
            selectedOptionIds
        }
        results {
            totalAnswers
            averageRating
            options {
                id
                text
                order
                count
            }
            textAnswers
            parentAnswers {
                parentName
                optionIds
                rating
                textAnswer
            }
        }
    }
`

export const GET_POSTS: DocumentNode = gql`
    ${POLL_FRAGMENT}
    query GetPosts(
        $daycareId: Int!
        $childIds: [Int!]
        $sectionIds: [Int!]
        $categoryIds: [Int!]
        $skip: Int
        $withoutSection: Boolean
        $draft: Boolean = false
    ) {
        getPosts(
            daycare_id: $daycareId
            child_ids: $childIds
            section_ids: $sectionIds
            category_ids: $categoryIds
            skip: $skip
            withoutSection: $withoutSection
            draft: $draft
        ) {
            hasMorePosts
            countDraftPosts
            posts {
                id
                message
                creationDate
                updateDate
                postedBy
                categories {
                    id
                    name
                }
                medias {
                    media {
                        id
                        type
                        fileName
                        creationDate
                        canDownload
                    }
                    base64Data
                }
                childIds
                views {
                    parentName
                    creationDate
                }
                reactions {
                    id
                    reactionType
                    parentName
                    creationDate
                }
                taskNoteTitle
                type
                taskNoteStatus
                taskNoteDate
                visibleAll
                poll {
                    ...PollFields
                }
            }
        }
    }
`

export const CREATE_POST: DocumentNode = gql`
    mutation CreatePost(
        $daycareId: Int!
        $message: String!
        $categoryIds: [Int!]
        $childIds: [Int!]
        $mediaIds: [Int!]
        $draft: Boolean
        $visibleAll: Boolean
        $poll: PollInput
    ) {
        createPost(
            daycare_id: $daycareId
            message: $message
            category_ids: $categoryIds
            child_ids: $childIds
            media_ids: $mediaIds
            draft: $draft
            visible_all: $visibleAll
            poll: $poll
        )
    }
`

export const CREATE_POST_TRUSTEE: DocumentNode = gql`
    mutation CreatePostTrustee(
        $daycareIds: [Int!]
        $sectionIds: [Int!]
        $withoutSectionDaycareIds: [Int!]
        $message: String!
        $categoryIds: [Int!]
        $mediaIds: [Int!]
        $draft: Boolean
        $visibleAll: Boolean
        $poll: PollInput
    ) {
        createPostTrustee(
            daycare_ids: $daycareIds
            section_ids: $sectionIds
            without_section_daycare_ids: $withoutSectionDaycareIds
            message: $message
            category_ids: $categoryIds
            media_ids: $mediaIds
            draft: $draft
            visible_all: $visibleAll
            poll: $poll
        )
    }
`

export const UPDATE_POST: DocumentNode = gql`
    mutation UpdatePost(
        $daycareId: Int!
        $postId: Int!
        $message: String!
        $categoryIds: [Int!]
        $childIds: [Int!]
        $mediaIds: [Int!]
        $visibleAll: Boolean
        $poll: PollInput
    ) {
        updatePost(
            daycare_id: $daycareId
            post_id: $postId
            message: $message
            category_ids: $categoryIds
            child_ids: $childIds
            media_ids: $mediaIds
            visible_all: $visibleAll
            poll: $poll
        )
    }
`

export const DELETE_POST: DocumentNode = gql`
    mutation DeletePost($daycareId: Int!, $postId: Int!) {
        deletePost(daycare_id: $daycareId, post_id: $postId)
    }
`

export const CREATE_MEDIA: DocumentNode = gql`
    mutation CreateMedia($daycareId: Int!, $file: Upload!, $postId: Int, $width: Int, $height: Int) {
        createMedia(daycare_id: $daycareId, file: $file, post_id: $postId, width: $width, height: $height) {
            id
            mediaType
        }
    }
`

export const DELETE_MEDIA: DocumentNode = gql`
    mutation DeleteMedia($daycareId: Int!, $mediaId: Int!) {
        deleteMedia(daycare_id: $daycareId, media_id: $mediaId)
    }
`

export const CREATE_REACTION: DocumentNode = gql`
    mutation CreateReaction($daycareId: Int!, $postId: Int!, $type: ReactionType!) {
        createReaction(daycare_id: $daycareId, post_id: $postId, type: $type)
    }
`

export const DELETE_REACTION: DocumentNode = gql`
    mutation DeleteReaction($daycareId: Int!, $reactionId: Int!) {
        deleteReaction(daycare_id: $daycareId, reaction_id: $reactionId)
    }
`

export const GET_MEDIA: DocumentNode = gql`
    query GetMedia($daycareId: Int!, $mediaId: Int!, $postId: Int!) {
        getMedia(daycareId: $daycareId, mediaId: $mediaId, postId: $postId) {
            media {
                id
                type
                fileName
                creationDate
                canDownload
            }
            base64Data
        }
    }
`

export const PUBLISH_POST: DocumentNode = gql`
    mutation PublishPost($daycareId: Int!, $postId: Int!) {
        publishPost(daycareId: $daycareId, postId: $postId)
    }
`

export const GET_POST_CATEGORIES: DocumentNode = gql`
    query GetPostCategories($daycareId: Int!) {
        getPostCategories(daycare_id: $daycareId) {
            id
            name
        }
    }
`

export const CREATE_POST_CATEGORY: DocumentNode = gql`
    mutation CreatePostCategory($daycareId: Int!, $name: String!) {
        createPostCategory(daycare_id: $daycareId, name: $name)
    }
`

export const UPDATE_POST_CATEGORY: DocumentNode = gql`
    mutation UpdatePostCategory($daycareId: Int!, $postCategoryId: Int!, $name: String!) {
        updatePostCategory(daycare_id: $daycareId, post_category_id: $postCategoryId, name: $name)
    }
`

export const DELETE_POST_CATEGORY: DocumentNode = gql`
    mutation DeletePostCategory($daycareId: Int!, $postCategoryId: Int!) {
        deletePostCategory(daycare_id: $daycareId, post_category_id: $postCategoryId)
    }
`

export const SUBMIT_POLL_ANSWER: DocumentNode = gql`
    mutation SubmitPollAnswer($daycareId: Int!, $postId: Int!, $textAnswer: String, $rating: Int, $optionIds: [Int!]) {
        submitPollAnswer(
            daycareId: $daycareId
            postId: $postId
            textAnswer: $textAnswer
            rating: $rating
            optionIds: $optionIds
        )
    }
`
